import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFileDownload } from 'react-icons/fa'
import { Form, Formik } from 'formik'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography, useTheme } from '@mui/material'
import { ExpandMore, FilePresent, Grading, People, RestartAlt } from '@mui/icons-material'
import { usePUT_submissionRequestResubmissionMutation } from 'redux/api/submissionsApi'
import { usePOST_assignmentAssessMutation } from 'redux/api/assignmentsApi'
import DialogPopup from 'components/molecules/DialogPopup'
import Rubric from 'components/molecules/Rubric'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SubmissionInfo from 'components/molecules/SubmissionInfo'
import SubmissionPeerAssessments from './SubmissionPeerAssessments'
import StudentLearningObjectivesAccordion from './StudentLearningObjectivesAccordion'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { initialValues, validationSchema } from 'utils/formModels/assignmentAssessModel'
import { formatDateTime } from 'utils/helpers'


const slideProps = {
  mountOnEnter: true,
  unmountOnExit: true,
  timeout: { enter: 225, exit: 195 }
};

const AssessRow = ({ student, assignmentData }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const {
    comment,
    grading_components,
    final,
    reported_time,
    observation,
    observed_at
  } = formModel()

  const [
      assessStudent,
      { isLoading: assessLoading, isSuccess: assessSuccess, error: assessError, data: assesseData },
   ] = usePOST_assignmentAssessMutation()
  useErrorAlert(assessError)
  useSuccessAlert(assessSuccess ? `${t('alerts.entity-saved', {entity: 'Assess data'})}` : '')
  
  const [requestResubmission, { isSuccess: resubmitSuccess, error: resubmitError, data: resubmit }] = usePUT_submissionRequestResubmissionMutation(assignmentData?.id)
  useErrorAlert(resubmitError)
  useSuccessAlert(resubmitSuccess ? t('alerts.resubmit-success') : '')
  
  const [action, setAction] = useState('')
  const [selectedSubmission, setSelectedSubmission] = useState(null)

  const final_grade_display = assignmentData?.score_type?.id === 'points' 
    ? (student.final_grade_points ? student.final_grade_points : t('common.not-graded'))
    : (student.final_grade_percentage ? `${student.final_grade_percentage}%` : t('common.not-graded'))

  const final_grade_value = assignmentData?.score_type?.id === 'points' 
    ? student.final_grade_points
    : student.final_grade_percentage 

  
  const serverValues = {
    comment: student?.comment ?? '',
    observation: student?.observation ?? '',
    observed_at: student?.observed_at ?? null,
    final: final_grade_value,
    final_display: final_grade_display,
    reported_time: student?.reported_time,
    rubric_grades: student?.rubric_grades ?? [],
    grading_components: student?.grading_components.map(component => {
        return {
          id: component.grading_component_id,
          title: component.grading_component.title, 

          grade: assignmentData?.score_type?.id === 'points' 
            ? component.grade_points
            : component.grade_percentage,

          grade_display: assignmentData?.score_type?.id === 'points' 
            ? (component.grade_points ? component.grade_points : t('common.not-graded'))
            : (component.grade_percentage ? `${component.grade_percentage}%` : t('common.not-graded'))
        }
    })
  }


  return (
    <>
      <Box
        className='table-cell'
        sx={{
          width: '25%',
        }}
      >
        <>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>{student.student.title}</Box>
        </>
      </Box>
      <Box
        className='table-cell'
        sx={{
          width: '20%',
        }}
      >
        <>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column' }}>
            {serverValues.grading_components.map((component) => {
              return (
                <div key={'title_'+component.id}>
                   {component.title}:  <b>{component.grade_display}</b>
                </div>
              )
            })}
          </Box>
        </>
      </Box>

      <Box
        className='table-cell'
        sx={{
          width: '15%',
        }}
      >
        <div>
            <b>{serverValues.final_display}</b>
        </div>
      </Box>

      <Box
        className='table-cell'
        sx={{
          width: '40%',
        }}
      >
        <>
          {(student.submissions.length>0 && <>
              <Button
                size='small'
                variant='contained'
                color='primary'
                className='small-button'
                sx={{ marginRight: '5px' }}
                onClick={() => {
                  setAction('submissions')
                }}
              >
                <FilePresent />
                {`${t('common.view-submission')}`}
              </Button>


              <Button
                size='small'
                variant='contained'
                color='success'
                className='small-button'
                sx={{ marginRight: '5px' }}
                onClick={() => {
                  setAction('assess')
                }}
              >
                <Grading />
                {`${t('common.assess-submission')}`}
              </Button>


              <DialogPopup
                title={student.student.title}
                open={action!==''}
                onClose={() => {
                  setAction('')
                }}
                popupWidth={'1000px'}
                >

                  {action === 'submissions' && <>
                    {
                        student.submissions.map((s, index) => 
                          <Accordion 
                            TransitionProps={slideProps}
                            key={'submission_'+s.id} 
                            defaultExpanded={index === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Typography>
                                 {t('common.submitted-on')}: {formatDateTime(s.created_at)}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>  
                                  <SubmissionInfo id={s.id} /> 
              
                                  {assignmentData?.has_peer_assessment &&
                                    <Button
                                      size='small'
                                      variant='contained'
                                      color='blue'
                                      className='small-button'
                                      sx={{ marginRight: '5px' }}
                                      onClick={() => {
                                        setSelectedSubmission(s.id)
                                        setAction('peer-assessments')
                                      }}
                                    >
                                      <People />
                                      {`${t('common.peer-assessment')}`}
                                    </Button>
                                    }

                                  <Button
                                    size='small'
                                    variant='contained'
                                    color='success'
                                    className='small-button'
                                    sx={{ marginRight: '5px' }}
                                    onClick={() => {
                                      setAction('assess')
                                    }}
                                  >
                                    <Grading />
                                    {`${t('common.assess-submission')}`}
                                  </Button>

                                  <Button
                                    size='small'
                                    variant='contained'
                                    color='neutral'
                                    className='small-button'
                                    onClick={() => requestResubmission(s.id)}
                                  >
                                    <RestartAlt />
                                    {`${t('common.request-resubmission')}`}
                                  </Button>
                                      

                            </AccordionDetails>
                          </Accordion>  
                        ) 
                      
                    }


                   </>
                  }

                  {action === 'peer-assessments' && <>

                      <SubmissionPeerAssessments submissionId={selectedSubmission} /> 

                      <div style={{ marginTop: '20px' }}>
                        <Button
                          size='small'
                          variant='contained'
                          color='primary'
                          className='small-button'
                          sx={{ marginRight: '5px' }}
                          onClick={() => {
                            setAction('submissions')
                          }}
                        >
                          <FilePresent />
                          {`${t('common.view-submission')}`}
                        </Button>
                        <Button
                          size='small'
                          variant='contained'
                          color='success'
                          className='small-button'
                          sx={{ marginRight: '5px' }}
                          onClick={() => {
                            setAction('assess')
                          }}
                        >
                          <Grading />
                          {`${t('common.assess-submission')}`}
                        </Button>
                      </div>
                   </>
                  }

                  {action === 'assess' && <>


                    <Formik
                        enableReinitialize
                        initialValues={serverValues}
                        validationSchema={validationSchema}
                        onSubmit={(values: any) => { 

                            const gradings = values.grading_components.map((component, index) => {
                              if (assignmentData.score_type?.id === 'points') {
                                return {
                                  grading_component_id: Number(assignmentData.grading_components[index].id),
                                  grade_points: component.grade && component.grade > 0 ? Number(component.grade ) : 0,
                                }
                              } else {
                                return {
                                  grading_component_id: Number(assignmentData.grading_components[index].id),
                                  grade_percentage: component.grade && component.grade > 0 ? Number(component.grade) : 0,
                                }
                              }
                            })

                            let studentData: any = {
                              teacher_id: Number(assignmentData.author_id),
                              assignment_id: Number(assignmentData.id),
                              student_id: Number(student.student.id),
                              reported_time: Number(values.reported_time),
                              comment: values.comment,
                              observation: values.observation,
                              observed_at: values.observed_at || null,
                              rubric_grades: values.rubric_grades,
                              grading_components: gradings,
                            }

                            if (assignmentData.score_type.id === 'points') studentData.final_grade_points = Number(values.final)
                            else studentData.final_grade_percentage = Number(values.final)
                            
                            const data = { teacher_id: assignmentData.author_id, students: [
                              studentData
                            ] }

                            assessStudent({ id: assignmentData?.id, data })
                        }}>
                        {({ values, setFieldValue }) => {
                        return (
                            <Form style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '40px' }}>

                              <StudentLearningObjectivesAccordion objectives={assignmentData?.student_learning_objectives} />
                              
                              {assignmentData?.rubric?.id &&
                                <div style={{ marginBottom: '20px' }}><Rubric 
                                    id={assignmentData?.rubric?.id} 
                                    extraCredit={assignmentData?.has_extra_credit ? assignmentData?.extra_credit : 0}
                                    rubricGrades={values.rubric_grades} 
                                    selectHandler={(newRubricGrades: any[], points: number, percent: number) => {
                                      setFieldValue("rubric_grades", newRubricGrades)

                                      if(assignmentData?.score_type?.id === 'points')
                                        setFieldValue(final.name, points)
                                      else
                                        setFieldValue(final.name, percent)
                                    }}/>
                                </div>
                              }

                              <Grid container spacing={4}>
                                {/* Written feedback and evidence */}
                                <Grid item xs={12} md={8}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <RichTextField
                                      width={'100%'}
                                      height={'150px'}
                                      fieldProps={{...comment}}
                                      label={comment.label}
                                    />
                                    <RichTextField
                                      width={'100%'}
                                      height={'150px'}
                                      fieldProps={{...observation}}
                                      label={observation.label}
                                    />
                                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                                      <DatePickerField fieldProps={{...observed_at}} />
                                    </Box>
                                  </Box>
                                </Grid>

                                {/* Grades */}
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {
                                      serverValues.grading_components.map((component, index) => {

                                        return <InputField
                                          valueSelector='grade'
                                          key={'grade_component_'+component.id}
                                          fieldProps={{
                                            label: component.title,
                                            name: `${grading_components.name}[${index}]`,
                                          }}
                                          sx={{ width: '100%' }}
                                        />
                                      })
                                    }

                                    <InputField
                                        fieldProps={{...reported_time }}
                                        sx={{ width: '100%' }}
                                    />

                                    <InputField
                                        fieldProps={{...final}}
                                        sx={{ width: '100%' }}
                                    />

                                    <SubmitButton
                                      color={'success'}
                                      text={`${t('common.save-assessment')}`}
                                      sx={{ width: '100%' }}
                                      isLoading={assessLoading}
                                      alignRight={false}
                                    />
                                  </Box>
                                </Grid>
                              </Grid>

                            </Form>
                            )
                           }}
                    </Formik>
                  </>}
                        
            
              </DialogPopup>
          


          
          </>) || t('common.no-submission')}
        
    
        </>
      </Box>

    </>
  )
}
export default AssessRow

